import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface PixverseV35T2vParams {
  /**
   * 正向提示词
   */
  prompt?: string;
  /**
   * 视频宽高比
   * @default "16:9"
   * @values 16:9 | 9:16 | 1:1 | 4:3 | 3:4
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1' | '4:3' | '3:4';
  /**
   * 视频时长，5或10秒
   * @default 5
   * @values 5 | 10
   */
  duration?: 5 | 10;
  /**
   * 视频质量
   * @default "540p"
   * @values 270p | 540p | 720p | 1080p
   */
  quality?: '270p' | '540p' | '720p' | '1080p';
  /**
   * 负向提示词
   */
  negative_prompt?: string;
  /**
   * 运动模式
   * @values normal | fast
   */
  motion_mode?: 'normal' | 'fast';
  /**
   * 摄像机运动
   * @values zoom_in | zoom_out | pan_left | pan_right | tilt_up | tilt_down
   */
  camera_movement?: 'zoom_in' | 'zoom_out' | 'pan_left' | 'pan_right' | 'tilt_up' | 'tilt_down';
  /**
   * 随机种子，用于可重现的生成结果
   */
  seed?: number;
  /**
   * 模板ID
   */
  template_id?: number;
}

export interface PixverseV35T2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * pixverse_v3_5_t2v
 *
 * PixVerse V3.5 文本生成视频接口
 * 
 * Model: v3.5 (固定模型)
 * Features: 文本生成视频功能
 * Duration: 5秒或10秒（默认5秒）
 * Quality: 270p、540p（默认）、720p、1080p
 * Aspect Ratio: 16:9（默认）、9:16、1:1、4:3、3:4
 * Motion Mode: normal、fast（快速）
 * Advanced: 支持摄像机运动控制、负向提示词
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, pixverseV35T2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await pixverseV35T2v({
 *   // 参数见 PixverseV35T2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 PixverseV35T2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function pixverseV35T2v(
  params: PixverseV35T2vParams,
): Promise<PixverseV35T2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'pixverse_v3_5_t2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: PixverseV35T2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
